/**
 * Locale resolution for the embed iframe SPA.
 *
 * Mirrors `resolveInitialLanguage` in `apps/WunderGuide/lang/i18n.ts` so
 * a partner request for `zh-CN` lands on `zh-Hans` regardless of which
 * client started the conversation.
 *
 * Resolution order (high → low):
 *   1. Aliases — e.g. `no` → `nb` (Norwegian Bokmål canonical).
 *   2. localeMap — explicit BCP-47 region → script tag (zh-CN → zh-Hans).
 *   3. Chinese region fallback — any zh-* not in localeMap defaults to
 *      zh-Hans (Simplified is the wider audience).
 *   4. Base language — strip the region (en-US → en).
 *   5. Fallback — 'en' if every step bails.
 *
 * Pure function, no side effects. Caller passes the resolved tag to
 * `i18n.changeLanguage()` (which then runs i18next's own resource lookup
 * — including languageBackendLoader for lazy-loaded JSONs).
 */
import localeMap from './localeMap.json';

const FALLBACK = 'en';

const LANGUAGE_ALIASES: Record<string, string> = {
  // Norwegian — `no` is a macro tag covering nb (Bokmål) + nn (Nynorsk);
  // Bokmål is the de-facto written standard for ~85% of Norwegians.
  no: 'nb',
};

const ZH_HANS_REGIONS = new Set(['CN', 'SG', 'MY']);
const ZH_HANT_REGIONS = new Set(['TW', 'HK', 'MO']);

const LOCALE_MAP = localeMap as Record<string, string>;

export function resolveLocale(tag: string | null | undefined): string {
  if (!tag || typeof tag !== 'string') return FALLBACK;
  const trimmed = tag.trim();
  if (!trimmed) return FALLBACK;

  // 1. Direct alias on the full tag.
  if (LANGUAGE_ALIASES[trimmed]) return LANGUAGE_ALIASES[trimmed];

  // 2. localeMap — explicit region/script overrides (zh-CN → zh-Hans, etc.).
  if (LOCALE_MAP[trimmed]) return LOCALE_MAP[trimmed];

  const [base, region] = trimmed.split('-');
  if (!base) return FALLBACK;

  // 3. Alias on the base language.
  if (LANGUAGE_ALIASES[base]) return LANGUAGE_ALIASES[base];

  // 4. Chinese region fallback — anything zh-* that isn't already in
  //    localeMap maps to a script tag based on region. Bare 'zh' defaults
  //    to Simplified (larger speaker base).
  if (base === 'zh') {
    if (region && ZH_HANS_REGIONS.has(region.toUpperCase())) return 'zh-Hans';
    if (region && ZH_HANT_REGIONS.has(region.toUpperCase())) return 'zh-Hant';
    return 'zh-Hans';
  }

  // 5. Strip region for everything else (en-US → en, ja-JP → ja).
  return base;
}
